import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { TIDS } from "@/constants/testIds";
import { ArrowLeft, Check, Eye, X, Palette, ArrowRight } from "lucide-react";
import { SectionView } from "@/components/pageSections";

export default function LandingPageNew() {
  const nav = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState("blank");
  const [colorIdx, setColorIdx] = useState({}); // per-template chosen color index
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [previewId, setPreviewId] = useState(null); // template id currently open in preview modal

  useEffect(() => {
    api.get("/templates").then(({ data }) => {
      setTemplates(data);
      // Default each template to color index 0
      const seed = {};
      data.forEach((t) => { seed[t.id] = 0; });
      setColorIdx(seed);
    }).catch(() => {});
  }, []);

  // Close preview on Escape
  useEffect(() => {
    if (!previewId) return;
    const onKey = (e) => { if (e.key === "Escape") setPreviewId(null); };
    window.addEventListener("keydown", onKey);
    // Lock body scroll while modal is open
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [previewId]);

  const create = async (e) => {
    e?.preventDefault?.();
    if (!title.trim()) { toast.error("Give your page a title first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/landing-pages", {
        title,
        template_id: selected,
        color_option: colorIdx[selected] ?? 0,
      });
      toast.success("Page created");
      nav(`/app/pages/${data.id}/edit`);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const previewTpl = previewId ? templates.find((t) => t.id === previewId) : null;
  const previewAccent = previewTpl
    ? (previewTpl.color_options?.[colorIdx[previewTpl.id] ?? 0]?.accent || previewTpl.accent_color || "#0B5CFF")
    : "#0B5CFF";

  return (
    <div className="space-y-8 vp-fade-up">
      <div>
        <Link to="/app/pages" className="inline-flex items-center gap-2 text-sm text-[#4A4D57] hover:text-[#0B5CFF]">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Back to pages
        </Link>
        <div className="vp-label mt-3">New landing page</div>
        <h1 className="font-display text-3xl font-bold mt-1">Pick a starting point</h1>
        <p className="text-[#4A4D57] mt-2 text-sm max-w-xl">
          {templates.length} niche-tuned templates. Every template comes with 3 color options — pick the palette that matches your brand. Click <Eye className="inline w-3.5 h-3.5" /> on any card to preview it live.
        </p>
      </div>

      <form onSubmit={create} className="space-y-6">
        <div className="max-w-md">
          <div className="vp-label mb-2">Page title (internal)</div>
          <input required className="vp-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="My coaching page" data-testid={TIDS.pageTitleInput} />
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {templates.map((t) => {
            const active = selected === t.id;
            const opts = t.color_options || [];
            const activeColor = opts[colorIdx[t.id] ?? 0]?.accent || t.accent_color || "#0B5CFF";
            return (
              <div
                key={t.id}
                data-testid={TIDS.templateCard + "-" + t.id}
                className={"text-left vp-card overflow-hidden transition-all cursor-pointer group " + (active ? "shadow-[0_10px_28px_rgba(11,92,255,0.18)]" : "hover:border-[#0B5CFF]/40")}
                style={active ? { borderColor: activeColor } : undefined}
                onClick={() => setSelected(t.id)}
              >
                <div className="aspect-[16/10] bg-[#F1F3F7] overflow-hidden relative">
                  <img src={t.preview} alt={t.name} className="w-full h-full object-cover" loading="lazy" />
                  {/* Accent bar overlay */}
                  <div className="absolute inset-x-0 bottom-0 h-1.5" style={{ background: activeColor }} />
                  {active && (
                    <div className="absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center" style={{ background: activeColor }}>
                      <Check className="w-4 h-4 text-white" strokeWidth={2.5} />
                    </div>
                  )}
                  {/* Live preview hover button */}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setSelected(t.id); setPreviewId(t.id); }}
                    data-testid={`preview-tpl-${t.id}`}
                    className="absolute inset-0 bg-black/50 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                  >
                    <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white text-[#0B0D17] font-semibold text-sm shadow-lg">
                      <Eye className="w-4 h-4" /> Live preview
                    </span>
                  </button>
                </div>
                <div className="p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-display font-semibold">{t.name}</div>
                      <div className="text-xs text-[#747680]">{t.category} · {t.sections.length} sections</div>
                    </div>
                  </div>
                  {opts.length > 0 && (
                    <div className="mt-3 flex items-center gap-1.5">
                      {opts.map((o, i) => {
                        const chosen = (colorIdx[t.id] ?? 0) === i;
                        return (
                          <button
                            key={i}
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelected(t.id);
                              setColorIdx((s) => ({ ...s, [t.id]: i }));
                            }}
                            title={o.name}
                            data-testid={`color-opt-${t.id}-${i}`}
                            className={"relative w-6 h-6 rounded-full border-2 transition-all " + (chosen ? "scale-110" : "border-white hover:scale-105")}
                            style={{ background: o.accent, borderColor: chosen ? o.accent : "#ffffff", boxShadow: chosen ? `0 0 0 2px ${o.accent}` : "0 0 0 1px #E4E6EC" }}
                          >
                            {chosen && <Check className="w-3 h-3 text-white absolute inset-0 m-auto" strokeWidth={3} />}
                          </button>
                        );
                      })}
                      <div className="text-[10px] text-[#747680] ml-1.5 truncate">{opts[colorIdx[t.id] ?? 0]?.name}</div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <button type="submit" disabled={busy || !title.trim()} className="vp-btn-primary" data-testid={TIDS.pageCreateSubmit}>
          {busy ? "Creating…" : "Create page"}
        </button>
      </form>

      {/* ============== LIVE PREVIEW MODAL ============== */}
      {previewTpl && (
        <div
          className="fixed inset-0 bg-black/80 z-[60] flex flex-col"
          data-testid="template-preview-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setPreviewId(null); }}
        >
          {/* Top bar */}
          <div className="flex-shrink-0 bg-white border-b border-[#E4E6EC] px-4 sm:px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3 min-w-0">
              <button
                onClick={() => setPreviewId(null)}
                className="p-2 rounded-lg hover:bg-[#F1F3F7]"
                aria-label="Close preview"
                data-testid="preview-modal-close"
              >
                <X className="w-5 h-5" strokeWidth={1.75} />
              </button>
              <div className="min-w-0">
                <div className="font-display font-bold text-base sm:text-lg truncate">{previewTpl.name}</div>
                <div className="text-xs text-[#747680] truncate">{previewTpl.category} · {previewTpl.sections.length} sections · Live preview</div>
              </div>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              {/* Color palette switcher inside preview */}
              {(previewTpl.color_options || []).length > 0 && (
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#F1F3F7]">
                  <Palette className="w-3.5 h-3.5 text-[#4A4D57]" strokeWidth={1.75} />
                  <div className="flex items-center gap-1.5">
                    {previewTpl.color_options.map((o, i) => {
                      const chosen = (colorIdx[previewTpl.id] ?? 0) === i;
                      return (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setColorIdx((s) => ({ ...s, [previewTpl.id]: i }))}
                          title={o.name}
                          data-testid={`preview-color-${previewTpl.id}-${i}`}
                          className={"relative w-6 h-6 rounded-full border-2 transition-all " + (chosen ? "scale-110" : "hover:scale-105")}
                          style={{ background: o.accent, borderColor: chosen ? o.accent : "#ffffff", boxShadow: chosen ? `0 0 0 2px ${o.accent}` : "0 0 0 1px #E4E6EC" }}
                        >
                          {chosen && <Check className="w-3 h-3 text-white absolute inset-0 m-auto" strokeWidth={3} />}
                        </button>
                      );
                    })}
                  </div>
                  <span className="text-xs text-[#4A4D57] font-medium hidden sm:inline">{previewTpl.color_options[colorIdx[previewTpl.id] ?? 0]?.name}</span>
                </div>
              )}

              <button
                type="button"
                onClick={() => { setPreviewId(null); create(); }}
                disabled={busy || !title.trim()}
                className="vp-btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-60"
                style={{ background: previewAccent, borderColor: previewAccent }}
                data-testid="preview-use-template"
              >
                Use this template <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Scrollable rendered preview */}
          <div className="flex-1 overflow-auto bg-white">
            <div className="min-h-full">
              {previewTpl.sections.map((s, idx) => (
                <SectionView key={idx} section={s} accent={previewAccent} />
              ))}
            </div>
          </div>

          {/* Bottom hint */}
          {!title.trim() && (
            <div className="flex-shrink-0 bg-[#FFF7ED] border-t border-[#FED7AA] px-4 py-2 text-center text-xs text-[#9A3412]">
              💡 Add a page title above before clicking "Use this template"
            </div>
          )}
        </div>
      )}
    </div>
  );
}
